import { useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  ContractStatus,
  MissionType,
  ProofType,
  type Channel,
  MissionAdminBrandsForCreateQuery,
  MissionAdminContractsByBrandQuery,
  MissionAdminContractsByBrandQueryVariables,
  MissionAdminMissionStepTemplatesForCreateQuery,
  MissionAdminMissionStepTemplatesForCreateQueryVariables,
} from "../../../../__generated__/graphql";
import {
  FiCamera,
  FiCreditCard,
  FiHeart,
  FiImage,
  FiMoreHorizontal,
  FiSearch,
  FiShoppingCart,
  FiStar,
  FiPackage,
  FiCoffee,
} from "react-icons/fi";

const CREATE_CAMPAIGN_DRAFT = gql`
  mutation MissionAdminCreateCampaignDraft(
    $input: MissionAdminCreateCampaignInput!
  ) {
    missionAdminCreateCampaignDraft(input: $input) {
      id
      brand {
        name
      }
      productName
      channel
      originalPrice
      discountPrice
      paybackPrice
    }
  }
`;

const SET_MISSION_STEPS = gql`
  mutation MissionAdminSetMissionSteps(
    $campaignId: Int!
    $steps: [MissionAdminMissionStepInput!]!
  ) {
    missionAdminSetMissionSteps(campaignId: $campaignId, steps: $steps) {
      id
      stepOrder
      type
      title
    }
  }
`;

const CREATE_RUN = gql`
  mutation MissionAdminCreateRun(
    $campaignId: Int!
    $input: MissionAdminCreateRunInput!
  ) {
    missionAdminCreateRun(campaignId: $campaignId, input: $input) {
      id
      startAt
      endAt
      depositAmount
      prizeAmount
      maxParticipants
    }
  }
`;

const PUBLISH_CAMPAIGN = gql`
  mutation MissionAdminPublishCampaign($campaignId: Int!) {
    missionAdminPublishCampaign(campaignId: $campaignId) {
      ok
      error
    }
  }
`;

const GET_TEMPLATES = gql`
  query MissionAdminMissionStepTemplatesForCreate(
    $channel: Channel
    $missionType: MissionType
  ) {
    missionAdminMissionStepTemplates(
      channel: $channel
      missionType: $missionType
    ) {
      id
      missionType
      channel
      title
      guide
      requirements
      notices
      proofType
    }
  }
`;

const GET_BRANDS = gql`
  query MissionAdminBrandsForCreate {
    missionAdminBrands {
      id
      name
    }
  }
`;

const GET_CONTRACTS_BY_BRAND = gql`
  query MissionAdminContractsByBrand($filter: MissionAdminContractFilter) {
    missionAdminContracts(filter: $filter) {
      id
      title
      totalBudget
      startAt
      endAt
      status
    }
  }
`;

interface MissionStepDraft {
  type: MissionType;
  title: string;
  guide: string[];
  requirements: string[];
  notices: string[];
  proofType: ProofType;
  isRequired: boolean;
  meta?: Record<string, unknown> | null;
  imageUrl: File | string | null;
}

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
};

const MissionAdminCampaignsCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [campaignId, setCampaignId] = useState<number | null>(null);

  // Step 1: 기본 정보
  const [basicInfo, setBasicInfo] = useState<{
    brandId: string;
    brandName: string;
    contractId: string;
    channel: string;
    productName: string;
    productImageUrl: File | string | null;
    productUrl: string;
    originalPrice: string;
    discountPrice: string;
    photoReviewRequired: boolean;
    eatingMethods: string[];
  }>({
    brandId: "",
    brandName: "",
    contractId: "",
    channel: "",
    productName: "",
    productImageUrl: null,
    productUrl: "",
    originalPrice: "",
    discountPrice: "",
    photoReviewRequired: false,
    eatingMethods: [],
  });
  const productUrlHref = (() => {
    const url = basicInfo.productUrl.trim();
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  })();

  // Step 2: 회차 (순서 변경)
  const [runInfo, setRunInfo] = useState({
    runType: "separate" as "separate" | "continuous" | "timeSelect", // 여러날 따로 생성 | 여러날 이어서 생성 | 시간선택
    selectedDates: [] as string[], // 여러날 따로 생성용 날짜 배열
    startDate: "", // 이어서 생성, 시간선택용
    endDate: "", // 이어서 생성, 시간선택용
    startTime: "", // 시간선택용
    endTime: "", // 시간선택용
    depositAmount: "",
    note: "",
    maxParticipantsType: "unlimited" as "unlimited" | "limited",
    maxParticipantsValue: "",
  });

  // Step 3: 미션 단계 (순서 변경)
  const [missionSteps, setMissionSteps] = useState<MissionStepDraft[]>([]);
  const [selectedMissionTypes, setSelectedMissionTypes] = useState<
    MissionType[]
  >([]);

  // 미션 타입 목록
  const missionTypeOptions = [
    { value: MissionType.Search, label: "검색", Icon: FiSearch },
    { value: MissionType.Like, label: "찜", Icon: FiHeart },
    { value: MissionType.AddToCart, label: "장바구니", Icon: FiShoppingCart },
    { value: MissionType.Purchase, label: "구매", Icon: FiCreditCard },
    { value: MissionType.Review, label: "리뷰", Icon: FiStar },
    { value: MissionType.PhotoReview, label: "포토리뷰", Icon: FiImage },
    { value: MissionType.UsageShot, label: "사용샷 인증", Icon: FiCamera },
    { value: MissionType.Etc, label: "기타", Icon: FiMoreHorizontal },
  ];

  // 선택된 미션 타입이 변경될 때 미션 스텝 업데이트
  const handleMissionTypeToggle = (missionType: MissionType) => {
    const isSelected = selectedMissionTypes.includes(missionType);
    let newSelectedTypes: MissionType[];

    if (isSelected) {
      // 선택 해제: 해당 타입의 미션 스텝 제거
      newSelectedTypes = selectedMissionTypes.filter((t) => t !== missionType);
      setMissionSteps((prev) =>
        prev.filter((step) => step.type !== missionType)
      );
    } else {
      // 선택: 새로운 미션 스텝 추가
      newSelectedTypes = [...selectedMissionTypes, missionType];

      // 템플릿 찾기
      const template = templatesData?.missionAdminMissionStepTemplates?.find(
        (t) =>
          t.missionType === missionType &&
          (t.channel === (basicInfo.channel as Channel) || !t.channel)
      );

      // 특정 미션 타입에 따라 proofType 자동 설정
      let proofType: ProofType = template?.proofType ?? ProofType.None;
      if (missionType === MissionType.UsageShot) {
        proofType = ProofType.Photo;
      } else if (
        missionType === MissionType.AddToCart ||
        missionType === MissionType.Review ||
        missionType === MissionType.PhotoReview
      ) {
        proofType = ProofType.Screenshot;
      }

      const newStep: MissionStepDraft = {
        type: missionType,
        title: template?.title ?? "",
        guide: toStringArray(template?.guide),
        requirements: toStringArray(template?.requirements),
        notices: toStringArray(template?.notices),
        proofType: proofType,
        isRequired: true,
        imageUrl: null,
      };

      setMissionSteps((prev) => [...prev, newStep]);
    }

    setSelectedMissionTypes(newSelectedTypes);
  };

  const [createDraft, { loading: creatingDraft }] = useMutation(
    CREATE_CAMPAIGN_DRAFT
  );
  const [setSteps, { loading: settingSteps }] = useMutation(SET_MISSION_STEPS);
  const [createRun, { loading: creatingRun }] = useMutation(CREATE_RUN);
  const [publishCampaign, { loading: publishing }] =
    useMutation(PUBLISH_CAMPAIGN);

  const { data: templatesData } = useQuery<
    MissionAdminMissionStepTemplatesForCreateQuery,
    MissionAdminMissionStepTemplatesForCreateQueryVariables
  >(GET_TEMPLATES, {
    variables: {
      channel: basicInfo.channel ? (basicInfo.channel as Channel) : undefined,
    },
    skip: !basicInfo.channel,
  });

  const { data: brandsData } =
    useQuery<MissionAdminBrandsForCreateQuery>(GET_BRANDS);

  const { data: contractsData } = useQuery<
    MissionAdminContractsByBrandQuery,
    MissionAdminContractsByBrandQueryVariables
  >(GET_CONTRACTS_BY_BRAND, {
    variables: {
      filter: basicInfo.brandId
        ? {
            brandId: parseInt(basicInfo.brandId),
            status: ContractStatus.Active,
          }
        : { status: ContractStatus.Active },
    },
    skip: !basicInfo.brandId,
  });

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!basicInfo.contractId) {
      alert("계약을 선택해주세요.");
      return;
    }

    if (basicInfo.eatingMethods.length === 0) {
      alert("먹는 방식을 최소 하나 이상 선택해주세요.");
      return;
    }

    const paybackPrice =
      parseInt(basicInfo.originalPrice) - parseInt(basicInfo.discountPrice);

    if (paybackPrice <= 0) {
      alert("정가가 할인가보다 커야 합니다.");
      return;
    }

    try {
      const { data } = await createDraft({
        variables: {
          input: {
            brandName: basicInfo.brandName,
            contractId: parseInt(basicInfo.contractId),
            channel: basicInfo.channel,
            category: null,
            productName: basicInfo.productName,
            productImageUrl:
              basicInfo.productImageUrl instanceof File
                ? basicInfo.productImageUrl
                : null,
            productUrl: basicInfo.productUrl || null,
            originalPrice: parseInt(basicInfo.originalPrice),
            discountPrice: parseInt(basicInfo.discountPrice),
            photoReviewRequired: basicInfo.photoReviewRequired,
            eatingMethods:
              basicInfo.eatingMethods.length > 0
                ? basicInfo.eatingMethods
                : null,
          },
        },
      });

      setCampaignId(data.missionAdminCreateCampaignDraft.id);
      setStep(2);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";
      alert(`에러: ${message}`);
    }
  };

  // Step 2: 회차(Run) 생성 (순서 변경)
  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Step 1에서 계산된 페이백 금액 사용
      const paybackPrice =
        parseInt(basicInfo.originalPrice) - parseInt(basicInfo.discountPrice);

      // maxParticipants 처리
      const maxParticipants =
        runInfo.maxParticipantsType === "limited" &&
        runInfo.maxParticipantsValue
          ? parseInt(runInfo.maxParticipantsValue)
          : null;

      if (runInfo.runType === "separate") {
        // 여러날 따로 생성: 선택한 날짜들 각각에 대해 회차 생성
        const validDates = runInfo.selectedDates.filter((date) => date !== "");
        if (validDates.length === 0) {
          alert("최소 하나 이상의 날짜를 선택해주세요.");
          return;
        }

        // 각 날짜에 대해 회차 생성
        for (const dateStr of validDates) {
          const selectedDate = new Date(dateStr);
          selectedDate.setHours(0, 0, 0, 0);
          const startAtISO = selectedDate.toISOString();

          const endDate = new Date(dateStr);
          endDate.setDate(endDate.getDate() + 1); // 다음날
          endDate.setHours(0, 0, 0, 0);
          const endAtISO = endDate.toISOString();

          await createRun({
            variables: {
              campaignId,
              input: {
                startAt: startAtISO,
                endAt: endAtISO,
                durationMinutes: null,
                depositAmount: parseInt(runInfo.depositAmount),
                prizeAmount: paybackPrice,
                note: runInfo.note || null,
                maxParticipants,
              },
            },
          });
        }
      } else if (runInfo.runType === "continuous") {
        // 여러날 이어서 생성: 하나의 회차 생성
        if (!runInfo.startDate || !runInfo.endDate) {
          alert("시작일과 종료일을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(runInfo.startDate);
        startDate.setHours(0, 0, 0, 0);
        const startAtISO = startDate.toISOString();

        const endDate = new Date(runInfo.endDate);
        endDate.setDate(endDate.getDate() + 1); // 다음날
        endDate.setHours(0, 0, 0, 0);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      } else {
        // 시간선택: 날짜 + 시간 지정
        if (
          !runInfo.startDate ||
          !runInfo.endDate ||
          !runInfo.startTime ||
          !runInfo.endTime
        ) {
          alert("시작일, 종료일, 시작 시간, 종료 시간을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(
          `${runInfo.startDate}T${runInfo.startTime}:00`
        );
        const startAtISO = startDate.toISOString();

        const endDate = new Date(`${runInfo.endDate}T${runInfo.endTime}:00`);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      }

      setStep(3);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";
      alert(`에러: ${message}`);
    }
  };

  // Step 3: 미션 단계 (순서 변경)
  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (missionSteps.length === 0) {
      alert("최소 1개 이상의 미션 단계가 필요합니다.");
      return;
    }

    try {
      await setSteps({
        variables: {
          campaignId,
          steps: missionSteps.map((step, idx) => ({
            stepOrder: idx + 1,
            type: step.type,
            title: step.title || null,
            guide: step.guide || [],
            requirements: step.requirements || [],
            notices: step.notices || [],
            proofType: step.proofType,
            isRequired: step.isRequired ?? true,
            meta: step.meta || null,
            imageUrl: step.imageUrl || null,
            // windowStartAt과 windowEndAt은 서버에서 run의 startAt/endAt을 사용하므로 전달하지 않음
          })),
        },
      });

      setStep(4);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";
      alert(`에러: ${message}`);
    }
  };

  const handlePublish = async () => {
    try {
      const { data } = await publishCampaign({
        variables: { campaignId },
      });

      if (data.missionAdminPublishCampaign.ok) {
        alert("캠페인이 공개되었습니다.");
        navigate("/ops/mission/campaigns");
      } else {
        alert(`에러: ${data.missionAdminPublishCampaign.error}`);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";
      alert(`에러: ${message}`);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">캠페인 생성</h1>

      {/* 진행 단계 표시 */}
      <div className="mb-8">
        <div className="flex items-center">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s}
                </div>
                <span className="mt-2 text-xs text-gray-600 text-center">
                  {s === 1 && "기본 정보"}
                  {s === 2 && "회차 생성"}
                  {s === 3 && "미션 단계"}
                  {s === 4 && "미리보기 & 공개"}
                </span>
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    step > s ? "bg-blue-600" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: 기본 정보 */}
      {step === 1 && (
        <form
          onSubmit={handleStep1Submit}
          className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto"
        >
          <h2 className="text-xl font-semibold mb-4">기본 정보 입력</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                브랜드 *
              </label>
              <select
                required
                value={basicInfo.brandId}
                onChange={(e) => {
                  const selectedBrand = brandsData?.missionAdminBrands?.find(
                    (b) => b.id.toString() === e.target.value
                  );
                  setBasicInfo({
                    ...basicInfo,
                    brandId: e.target.value,
                    brandName: selectedBrand?.name || "",
                    contractId: "", // 브랜드 변경 시 계약 선택 초기화
                  });
                }}
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              >
                <option value="">선택하세요</option>
                {brandsData?.missionAdminBrands?.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              {brandsData?.missionAdminBrands?.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  등록된 브랜드가 없습니다.{" "}
                  <a
                    href="/ops/mission/brands"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    브랜드 관리 페이지
                  </a>
                  에서 먼저 브랜드를 생성해주세요.
                </p>
              )}
            </div>

            {basicInfo.brandId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  계약 *{" "}
                  {contractsData?.missionAdminContracts?.length === 0 && (
                    <span className="text-red-500 text-xs">
                      (ACTIVE 계약이 없습니다)
                    </span>
                  )}
                </label>
                <select
                  required
                  value={basicInfo.contractId}
                  onChange={(e) =>
                    setBasicInfo({ ...basicInfo, contractId: e.target.value })
                  }
                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                  disabled={!contractsData?.missionAdminContracts?.length}
                >
                  <option value="">선택하세요</option>
                  {contractsData?.missionAdminContracts?.map((contract) => (
                    <option key={contract.id} value={contract.id}>
                      {contract.title} (예산:{" "}
                      {contract.totalBudget.toLocaleString()}원)
                    </option>
                  ))}
                </select>
                {contractsData?.missionAdminContracts?.length === 0 && (
                  <p className="mt-1 text-sm text-gray-500">
                    이 브랜드에 ACTIVE 상태인 계약이 없습니다.{" "}
                    <a
                      href="/ops/mission/contracts/create"
                      className="text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      계약 생성 페이지
                    </a>
                    에서 먼저 계약을 생성해주세요.
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                채널 *
              </label>
              <select
                required
                value={basicInfo.channel}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, channel: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              >
                <option value="">선택하세요</option>
                <option value="NAVER">네이버</option>
                <option value="COUPANG">쿠팡</option>
                <option value="CARROT">당근</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품명 *
              </label>
              <input
                type="text"
                required
                value={basicInfo.productName}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, productName: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품 이미지
              </label>
              <div className="space-y-3">
                {/* 기존 이미지 또는 선택한 파일 미리보기 표시 */}
                {basicInfo.productImageUrl && (
                  <div className="relative">
                    <img
                      src={
                        typeof basicInfo.productImageUrl === "string"
                          ? basicInfo.productImageUrl
                          : URL.createObjectURL(basicInfo.productImageUrl)
                      }
                      alt="제품 이미지"
                      className="w-full max-w-md rounded-lg object-cover border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // File 객체인 경우 URL 해제
                        if (basicInfo.productImageUrl instanceof File) {
                          URL.revokeObjectURL(
                            URL.createObjectURL(basicInfo.productImageUrl)
                          );
                        }
                        setBasicInfo({
                          ...basicInfo,
                          productImageUrl: null,
                        });
                      }}
                      className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                    >
                      이미지 삭제
                    </button>
                  </div>
                )}
                {/* 파일 업로드 */}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // 기존 File 객체가 있으면 URL 해제
                        if (basicInfo.productImageUrl instanceof File) {
                          URL.revokeObjectURL(
                            URL.createObjectURL(basicInfo.productImageUrl)
                          );
                        }
                        setBasicInfo({
                          ...basicInfo,
                          productImageUrl: file,
                        });
                      }
                    }}
                    className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    이미지 파일을 선택하세요 (JPG, PNG 등)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품 바로가기 URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={basicInfo.productUrl}
                  onChange={(e) =>
                    setBasicInfo({ ...basicInfo, productUrl: e.target.value })
                  }
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  disabled={!productUrlHref}
                  onClick={() => {
                    if (!productUrlHref) return;
                    window.open(productUrlHref, "_blank", "noreferrer");
                  }}
                  className={`px-4 py-3 rounded-md text-sm whitespace-nowrap ${
                    productUrlHref
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  미리보기
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  정가 (원) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={basicInfo.originalPrice}
                  onChange={(e) =>
                    setBasicInfo({
                      ...basicInfo,
                      originalPrice: e.target.value,
                    })
                  }
                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  할인가 (원) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={basicInfo.discountPrice}
                  onChange={(e) =>
                    setBasicInfo({
                      ...basicInfo,
                      discountPrice: e.target.value,
                    })
                  }
                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {basicInfo.originalPrice && basicInfo.discountPrice && (
              <div className="p-4 bg-blue-50 rounded-md">
                <div className="text-sm text-gray-700">
                  페이백 금액:{" "}
                  <span className="font-bold text-blue-600">
                    {(
                      parseInt(basicInfo.originalPrice) -
                      parseInt(basicInfo.discountPrice)
                    ).toLocaleString()}
                    원
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="photoReviewRequired"
                checked={basicInfo.photoReviewRequired}
                onChange={(e) =>
                  setBasicInfo({
                    ...basicInfo,
                    photoReviewRequired: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <label
                htmlFor="photoReviewRequired"
                className="text-sm font-medium text-gray-700"
              >
                포토리뷰 필수
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                먹는 방식 *{" "}
                <span className="text-gray-500 font-normal">
                  (복수 선택가능)
                </span>
              </label>
              <p className="text-xs text-gray-500 mb-2">*유저에게 노출됩니다</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const isSelected = basicInfo.eatingMethods.includes("포장");
                    if (isSelected) {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: basicInfo.eatingMethods.filter(
                          (m) => m !== "포장"
                        ),
                      });
                    } else {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: [...basicInfo.eatingMethods, "포장"],
                      });
                    }
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    basicInfo.eatingMethods.includes("포장")
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <FiPackage className="text-base" />
                  <span>포장</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const isSelected =
                      basicInfo.eatingMethods.includes("매장식사");
                    if (isSelected) {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: basicInfo.eatingMethods.filter(
                          (m) => m !== "매장식사"
                        ),
                      });
                    } else {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: [...basicInfo.eatingMethods, "매장식사"],
                      });
                    }
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    basicInfo.eatingMethods.includes("매장식사")
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <FiCoffee className="text-base" />
                  <span>매장식사</span>
                </button>
              </div>
              {basicInfo.eatingMethods.length === 0 && (
                <p className="mt-1 text-xs text-red-500">
                  최소 하나 이상 선택해주세요.
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creatingDraft}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingDraft ? "저장 중..." : "다음 단계"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Step 2: 회차 생성 (순서 변경) */}
      {step === 2 && (
        <form
          onSubmit={handleStep2Submit}
          className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto"
        >
          <div className="space-y-4">
            {/* 회차 선택 방식 (네모 버튼) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                회차 선택 방식 *
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRunInfo({
                      ...runInfo,
                      runType: "separate",
                    });
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                    runInfo.runType === "separate"
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  여러날 따로 생성
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRunInfo({
                      ...runInfo,
                      runType: "continuous",
                    });
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                    runInfo.runType === "continuous"
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  여러날 이어서 생성
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRunInfo({
                      ...runInfo,
                      runType: "timeSelect",
                    });
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                    runInfo.runType === "timeSelect"
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  커스터마이징
                </button>
              </div>
            </div>

            {runInfo.runType === "separate" ? (
              // 여러날 따로 생성: 여러 날짜 멀티 셀렉
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  날짜 선택 * (여러 날짜 선택 가능)
                </label>
                <div className="space-y-2">
                  {runInfo.selectedDates.filter((d) => d).length === 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const now = new Date();
                        const localNow = new Date(
                          now.getTime() - now.getTimezoneOffset() * 60 * 1000
                        );
                        const today = localNow.toISOString().split("T")[0];

                        setRunInfo((prev) => {
                          if (prev.selectedDates.includes(today)) return prev;

                          const firstEmptyIdx = prev.selectedDates.findIndex(
                            (d) => !d
                          );
                          if (firstEmptyIdx >= 0) {
                            const newDates = [...prev.selectedDates];
                            newDates[firstEmptyIdx] = today;
                            return { ...prev, selectedDates: newDates };
                          }

                          return {
                            ...prev,
                            selectedDates: [...prev.selectedDates, today],
                          };
                        });
                      }}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      오늘
                    </button>
                  )}
                  {runInfo.selectedDates.map((date, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                          const newDates = [...runInfo.selectedDates];
                          newDates[index] = e.target.value;
                          setRunInfo({
                            ...runInfo,
                            selectedDates: newDates,
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                      />
                      {date && (
                        <button
                          type="button"
                          onClick={() => {
                            // 현재 날짜에서 하루를 더한 날짜 계산
                            const currentDate = new Date(date);
                            currentDate.setDate(currentDate.getDate() + 1);
                            const nextDate = currentDate
                              .toISOString()
                              .split("T")[0];

                            const newDates = [...runInfo.selectedDates];
                            newDates.splice(index + 1, 0, nextDate);
                            setRunInfo({
                              ...runInfo,
                              selectedDates: newDates,
                            });
                          }}
                          className="px-3 py-2 text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50"
                        >
                          하루 추가
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const newDates = runInfo.selectedDates.filter(
                            (_, i) => i !== index
                          );
                          setRunInfo({
                            ...runInfo,
                            selectedDates: newDates,
                          });
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setRunInfo({
                        ...runInfo,
                        selectedDates: [...runInfo.selectedDates, ""],
                      });
                    }}
                    className="w-full px-4 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                  >
                    + 날짜 추가
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  선택한 각 날짜마다 별도의 회차가 생성됩니다 (00:00 ~ 다음날
                  00:00)
                </p>
              </div>
            ) : runInfo.runType === "continuous" ? (
              // 여러날 이어서 생성: 시작일과 종료일만 선택
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      시작일 *
                    </label>
                    <input
                      type="date"
                      required
                      value={runInfo.startDate}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, startDate: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      종료일 (포함) *
                    </label>
                    <input
                      type="date"
                      required
                      value={runInfo.endDate}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, endDate: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  시작일부터 종료일까지 하나의 회차가 생성됩니다 (시작일 00:00 ~
                  종료일 다음날 00:00)
                </p>
              </div>
            ) : (
              // 시간선택: 날짜 + 시간 지정
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      시작일 *
                    </label>
                    <input
                      type="date"
                      required
                      value={runInfo.startDate}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, startDate: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      시작 시간 *
                    </label>
                    <input
                      type="time"
                      required
                      step="1800"
                      value={runInfo.startTime}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, startTime: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      종료일 *
                    </label>
                    <input
                      type="date"
                      required
                      value={runInfo.endDate}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, endDate: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      종료 시간 *
                    </label>
                    <input
                      type="time"
                      required
                      step="1800"
                      value={runInfo.endTime}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, endTime: e.target.value })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                예치금 (원) *
              </label>
              <input
                type="number"
                required
                min="0"
                value={runInfo.depositAmount}
                onChange={(e) =>
                  setRunInfo({ ...runInfo, depositAmount: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              />
            </div>

            {/* 페이백 금액 표시 (Step 1에서 계산된 값) */}
            {basicInfo.originalPrice && basicInfo.discountPrice && (
              <div className="p-4 bg-blue-50 rounded-md">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">상금(페이백):</span>{" "}
                  <span className="font-bold text-blue-600">
                    {(
                      parseInt(basicInfo.originalPrice) -
                      parseInt(basicInfo.discountPrice)
                    ).toLocaleString()}
                    원
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    (Step 1에서 계산된 값이 자동으로 적용됩니다)
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                참여 인원수 제한
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="maxParticipants-unlimited-create"
                    name="maxParticipants-create"
                    value="unlimited"
                    checked={runInfo.maxParticipantsType === "unlimited"}
                    onChange={(e) =>
                      setRunInfo({
                        ...runInfo,
                        maxParticipantsType: e.target.value as
                          | "unlimited"
                          | "limited",
                      })
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="maxParticipants-unlimited-create"
                    className="ml-2 text-sm text-gray-700"
                  >
                    무제한
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="maxParticipants-limited-create"
                    name="maxParticipants-create"
                    value="limited"
                    checked={runInfo.maxParticipantsType === "limited"}
                    onChange={(e) =>
                      setRunInfo({
                        ...runInfo,
                        maxParticipantsType: e.target.value as
                          | "unlimited"
                          | "limited",
                      })
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="maxParticipants-limited-create"
                    className="ml-2 text-sm text-gray-700"
                  >
                    제한 설정
                  </label>
                  {runInfo.maxParticipantsType === "limited" && (
                    <input
                      type="number"
                      min="1"
                      value={runInfo.maxParticipantsValue}
                      onChange={(e) =>
                        setRunInfo({
                          ...runInfo,
                          maxParticipantsValue: e.target.value,
                        })
                      }
                      className="ml-3 px-3 py-2 border border-gray-300 rounded-md w-32"
                      placeholder="최대 인원수"
                      required={runInfo.maxParticipantsType === "limited"}
                    />
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                운영 메모
              </label>
              <textarea
                value={runInfo.note}
                onChange={(e) =>
                  setRunInfo({ ...runInfo, note: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                이전
              </button>
              <button
                type="submit"
                disabled={creatingRun}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingRun ? "저장 중..." : "다음 단계"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Step 3: 미션 단계 (순서 변경) */}
      {step === 3 && (
        <form
          onSubmit={handleStep3Submit}
          className="bg-white p-6 rounded-lg shadow w-full max-w-none"
        >
          <h2 className="text-xl font-semibold mb-4">미션 단계 구성</h2>

          {/* 미션 추가 */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4">미션 추가</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {missionTypeOptions.map((option) => {
                const isSelected = selectedMissionTypes.includes(option.value);
                const selectedOrder = isSelected
                  ? selectedMissionTypes.indexOf(option.value) + 1
                  : null;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleMissionTypeToggle(option.value)}
                    className={`relative px-4 py-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "bg-green-600 border-green-600 text-white shadow-md"
                        : "bg-green-50 border-green-200 text-green-700 hover:border-green-400 hover:bg-green-100"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <option.Icon className="text-base" aria-hidden />
                      <div className="font-medium text-sm">{option.label}</div>
                    </div>
                    {selectedOrder !== null && (
                      <>
                        <div className="mt-1 text-xs opacity-90">
                          {selectedOrder}번째
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-green-700 flex items-center justify-center text-xs font-bold border border-green-200 shadow">
                          {selectedOrder}
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedMissionTypes.length > 0 && (
              <p className="mt-3 text-sm text-gray-600">
                {selectedMissionTypes.length}개의 미션이 선택되었습니다.
              </p>
            )}
          </div>

          {/* 미션 단계 구성 */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold mb-4">미션 단계 구성</h3>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
              {missionSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-semibold">
                      {idx + 1}. {step.title || step.type}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const removedStep = missionSteps[idx];
                          const newSteps = [...missionSteps];
                          newSteps.splice(idx, 1);
                          setMissionSteps(newSteps);
                          // selectedMissionTypes에서도 제거
                          setSelectedMissionTypes((prev) =>
                            prev.filter((type) => type !== removedStep.type)
                          );
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    타입: {step.type} | 인증: {step.proofType}
                  </div>

                  {/* 편집 섹션: 항상 펼침 */}
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                    {/* 타이틀 편집 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        타이틀
                      </label>
                      <input
                        type="text"
                        value={step.title || ""}
                        onChange={(e) => {
                          const newSteps = [...missionSteps];
                          newSteps[idx] = {
                            ...newSteps[idx],
                            title: e.target.value,
                          };
                          setMissionSteps(newSteps);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="미션 스텝 타이틀을 입력하세요"
                      />
                    </div>

                    {/* Guide 편집 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        가이드 (단계별 안내)
                      </label>
                      <div className="space-y-2">
                        {(step.guide || []).map(
                          (item: string, guideIdx: number) => (
                            <div key={guideIdx} className="flex gap-2">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => {
                                  const newSteps = [...missionSteps];
                                  const newGuide = [
                                    ...(newSteps[idx].guide || []),
                                  ];
                                  newGuide[guideIdx] = e.target.value;
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    guide: newGuide,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="가이드 항목 입력"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newSteps = [...missionSteps];
                                  const newGuide = [
                                    ...(newSteps[idx].guide || []),
                                  ];
                                  newGuide.splice(guideIdx, 1);
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    guide: newGuide,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                              >
                                삭제
                              </button>
                            </div>
                          )
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const newSteps = [...missionSteps];
                            const newGuide = [
                              ...(newSteps[idx].guide || []),
                              "",
                            ];
                            newSteps[idx] = {
                              ...newSteps[idx],
                              guide: newGuide,
                            };
                            setMissionSteps(newSteps);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + 가이드 항목 추가
                        </button>
                      </div>
                    </div>

                    {/* Requirements 편집 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        필수 요구사항
                      </label>
                      <div className="space-y-2">
                        {(step.requirements || []).map(
                          (item: string, reqIdx: number) => (
                            <div key={reqIdx} className="flex gap-2">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => {
                                  const newSteps = [...missionSteps];
                                  const newRequirements = [
                                    ...(newSteps[idx].requirements || []),
                                  ];
                                  newRequirements[reqIdx] = e.target.value;
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    requirements: newRequirements,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="필수 요구사항 입력"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newSteps = [...missionSteps];
                                  const newRequirements = [
                                    ...(newSteps[idx].requirements || []),
                                  ];
                                  newRequirements.splice(reqIdx, 1);
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    requirements: newRequirements,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                              >
                                삭제
                              </button>
                            </div>
                          )
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const newSteps = [...missionSteps];
                            const newRequirements = [
                              ...(newSteps[idx].requirements || []),
                              "",
                            ];
                            newSteps[idx] = {
                              ...newSteps[idx],
                              requirements: newRequirements,
                            };
                            setMissionSteps(newSteps);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + 요구사항 추가
                        </button>
                      </div>
                    </div>

                    {/* Notices 편집 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        주의사항 (꼭 알아야 할 점)
                      </label>
                      <div className="space-y-2">
                        {(step.notices || []).map(
                          (item: string, noticeIdx: number) => (
                            <div key={noticeIdx} className="flex gap-2">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => {
                                  const newSteps = [...missionSteps];
                                  const newNotices = [
                                    ...(newSteps[idx].notices || []),
                                  ];
                                  newNotices[noticeIdx] = e.target.value;
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    notices: newNotices,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="주의사항 입력"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newSteps = [...missionSteps];
                                  const newNotices = [
                                    ...(newSteps[idx].notices || []),
                                  ];
                                  newNotices.splice(noticeIdx, 1);
                                  newSteps[idx] = {
                                    ...newSteps[idx],
                                    notices: newNotices,
                                  };
                                  setMissionSteps(newSteps);
                                }}
                                className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                              >
                                삭제
                              </button>
                            </div>
                          )
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const newSteps = [...missionSteps];
                            const newNotices = [
                              ...(newSteps[idx].notices || []),
                              "",
                            ];
                            newSteps[idx] = {
                              ...newSteps[idx],
                              notices: newNotices,
                            };
                            setMissionSteps(newSteps);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + 주의사항 추가
                        </button>
                      </div>
                    </div>

                    {/* 안내 이미지 업로드 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        안내 이미지
                      </label>
                      <div className="space-y-3">
                        {/* 기존 이미지 또는 선택한 파일 미리보기 표시 */}
                        {step.imageUrl && (
                          <div className="relative">
                            <img
                              src={
                                typeof step.imageUrl === "string"
                                  ? step.imageUrl
                                  : URL.createObjectURL(step.imageUrl)
                              }
                              alt="안내 이미지"
                              className="w-full max-w-md rounded-lg object-cover border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSteps = [...missionSteps];
                                // File 객체인 경우 URL 해제
                                const currentImageUrl = newSteps[idx].imageUrl;
                                if (currentImageUrl instanceof File) {
                                  URL.revokeObjectURL(
                                    URL.createObjectURL(currentImageUrl)
                                  );
                                }
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  imageUrl: null,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                            >
                              이미지 삭제
                            </button>
                          </div>
                        )}
                        {/* 파일 업로드 */}
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const newSteps = [...missionSteps];
                                // 기존 File 객체가 있으면 URL 해제
                                const currentImageUrl = newSteps[idx].imageUrl;
                                if (currentImageUrl instanceof File) {
                                  URL.revokeObjectURL(
                                    URL.createObjectURL(currentImageUrl)
                                  );
                                }
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  imageUrl: file,
                                };
                                setMissionSteps(newSteps);
                              }
                            }}
                            className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            이미지 파일을 선택하세요 (JPG, PNG 등)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Window 설정 안내 */}
                    <div className="p-3 bg-blue-50 rounded-md">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">제출 기간:</span>{" "}
                        회차(Run)의 시작 시간과 종료 시간이 자동으로 적용됩니다.
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {missionSteps.length === 0 && (
                <p className="text-gray-500 text-center py-8">
                  미션 단계를 추가해주세요.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              이전
            </button>
            <button
              type="submit"
              disabled={settingSteps || missionSteps.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {settingSteps ? "저장 중..." : "다음 단계"}
            </button>
          </div>
        </form>
      )}

      {/* Step 4: 미리보기 & 공개 */}
      {step === 4 && (
        <div className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto">
          <h2 className="text-xl font-semibold mb-4">미리보기 & 공개</h2>

          <div className="space-y-6 mb-6">
            {/* 캠페인 기본 정보 */}
            <div className="p-4 bg-gray-50 rounded-md">
              <h3 className="font-semibold mb-3 text-lg">캠페인 정보</h3>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">브랜드:</span>{" "}
                    {basicInfo.brandName}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">채널:</span>{" "}
                    {basicInfo.channel === "NAVER"
                      ? "네이버"
                      : basicInfo.channel === "COUPANG"
                      ? "쿠팡"
                      : basicInfo.channel === "CARROT"
                      ? "당근"
                      : basicInfo.channel}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">제품명:</span>{" "}
                    {basicInfo.productName}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-700">가격:</span>{" "}
                  <span className="line-through text-gray-500">
                    {parseInt(basicInfo.originalPrice).toLocaleString()}원
                  </span>{" "}
                  →{" "}
                  <span className="font-bold text-blue-600">
                    {parseInt(basicInfo.discountPrice).toLocaleString()}원
                  </span>{" "}
                  (페이백:{" "}
                  {(
                    parseInt(basicInfo.originalPrice) -
                    parseInt(basicInfo.discountPrice)
                  ).toLocaleString()}
                  원)
                </div>
                {basicInfo.photoReviewRequired && (
                  <div className="text-blue-600 font-medium">
                    ✓ 포토리뷰 필수
                  </div>
                )}
              </div>
            </div>

            {/* 회차(Run) 정보 */}
            <div className="p-4 bg-blue-50 rounded-md">
              <h3 className="font-semibold mb-3 text-lg">회차 정보</h3>
              <div className="space-y-2 text-sm">
                {runInfo.runType === "separate" ? (
                  <div>
                    <span className="font-medium text-gray-700">
                      여러날 따로 생성:
                    </span>{" "}
                    {runInfo.selectedDates.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {runInfo.selectedDates.map((date, idx) => (
                          <div key={idx} className="text-gray-600">
                            • {date} (00:00 ~ 다음날 00:00)
                          </div>
                        ))}
                        <div className="text-xs text-gray-500 mt-2">
                          총 {runInfo.selectedDates.length}개의 회차가
                          생성됩니다
                        </div>
                      </div>
                    ) : (
                      "-"
                    )}
                  </div>
                ) : runInfo.runType === "continuous" ? (
                  <div>
                    <span className="font-medium text-gray-700">
                      여러날 이어서 생성:
                    </span>{" "}
                    {runInfo.startDate && runInfo.endDate
                      ? `${runInfo.startDate} ~ ${runInfo.endDate} (00:00 ~ 다음날 00:00)`
                      : "-"}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-700">
                        시작 시간:
                      </span>{" "}
                      {runInfo.startDate && runInfo.startTime
                        ? `${runInfo.startDate} ${runInfo.startTime}`
                        : "-"}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        종료 시간:
                      </span>{" "}
                      {runInfo.endDate && runInfo.endTime
                        ? `${runInfo.endDate} ${runInfo.endTime}`
                        : "-"}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">예치금:</span>{" "}
                    <span className="font-bold text-red-600">
                      {parseInt(runInfo.depositAmount || "0").toLocaleString()}
                      원
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      상금(페이백):
                    </span>{" "}
                    <span className="font-bold text-green-600">
                      {(
                        parseInt(basicInfo.originalPrice) -
                        parseInt(basicInfo.discountPrice)
                      ).toLocaleString()}
                      원
                    </span>
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    참여 인원수 제한:
                  </span>{" "}
                  {runInfo.maxParticipantsType === "limited" &&
                  runInfo.maxParticipantsValue ? (
                    <span className="font-bold text-blue-600">
                      최대{" "}
                      {parseInt(runInfo.maxParticipantsValue).toLocaleString()}
                      명
                    </span>
                  ) : (
                    <span className="text-gray-600">무제한</span>
                  )}
                </div>
                {runInfo.note && (
                  <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                    <span className="font-medium text-gray-700">
                      운영 메모:
                    </span>
                    <div className="mt-1 text-gray-600 whitespace-pre-wrap">
                      {runInfo.note}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 미션 단계 정보 */}
            <div className="p-4 bg-green-50 rounded-md">
              <h3 className="font-semibold mb-3 text-lg">
                미션 단계 ({missionSteps.length}개)
              </h3>
              <div className="space-y-4">
                {missionSteps.map((step, idx) => {
                  const getMissionTypeLabel = (type: string) => {
                    const labels: Record<string, string> = {
                      SEARCH: "검색",
                      LIKE: "찜",
                      ADD_TO_CART: "장바구니",
                      PURCHASE: "구매",
                      REVIEW: "리뷰",
                      PHOTO_REVIEW: "포토리뷰",
                      USAGE_SHOT: "사용샷 인증",
                      ETC: "기타",
                    };
                    return labels[type] || type;
                  };

                  const getProofTypeLabel = (type: string) => {
                    const labels: Record<string, string> = {
                      NONE: "없음",
                      SCREENSHOT: "스크린샷",
                      PHOTO: "사진",
                      TEXT: "텍스트",
                      URL: "URL",
                    };
                    return labels[type] || type;
                  };

                  return (
                    <div
                      key={idx}
                      className="p-3 bg-white rounded border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-semibold text-base">
                            {idx + 1}.{" "}
                            {step.title || getMissionTypeLabel(step.type)}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            타입: {getMissionTypeLabel(step.type)} | 인증:{" "}
                            {getProofTypeLabel(step.proofType)}
                            {step.isRequired === false && " | 선택사항"}
                          </div>
                        </div>
                      </div>

                      {/* 가이드 */}
                      {step.guide &&
                        Array.isArray(step.guide) &&
                        step.guide.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              가이드:
                            </div>
                            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                              {step.guide.map((g: string, guideIdx: number) => (
                                <li key={guideIdx}>{g}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                      {/* 필수 요구사항 */}
                      {step.requirements &&
                        Array.isArray(step.requirements) &&
                        step.requirements.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              필수 요구사항:
                            </div>
                            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                              {step.requirements.map(
                                (req: string, reqIdx: number) => (
                                  <li key={reqIdx}>{req}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}

                      {/* 주의사항 */}
                      {step.notices &&
                        Array.isArray(step.notices) &&
                        step.notices.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm font-medium text-orange-700 mb-1">
                              주의사항:
                            </div>
                            <ul className="list-disc list-inside text-sm text-orange-600 space-y-1">
                              {step.notices.map(
                                (notice: string, noticeIdx: number) => (
                                  <li key={noticeIdx}>{notice}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}

                      {/* 제출 기간 안내 */}
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="text-sm font-medium text-blue-700 mb-1">
                          제출 기간:
                        </div>
                        <div className="text-xs text-blue-600">
                          회차(Run)의 시작 시간과 종료 시간이 자동으로
                          적용됩니다.
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(3)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              이전
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {publishing ? "공개 중..." : "캠페인 공개"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionAdminCampaignsCreate;
